import { Textfield } from '@simplywallst/ui-core';
import type { FormEvent, ReactNode } from 'react';
import { useDeferredValue, useEffect, useState } from 'react';
import useResizeObserver from 'use-resize-observer';
import { Popper } from './Popper';
import { parseNumberSafe } from '../../../../Narrative/utils';
import {
  isValidNumber,
  processRealNumberInput,
  roundNumber,
  stripFractionsExceedingDecimals,
} from '@/pages/Narrative/utils/number';
import { cn } from '@simplywallst/ui-core/next';

type Props = {
  value: number;
  precision: number;
  suffix: ReactNode;
  onChange: (value: number) => void;
  isValueValid: (value: number) => boolean;
  anchorEl: Element | null;
  boundaryEl: Element | null;
  id: string;
};

const INPUT_PADDING_BASE = 12;
const INPUT_PADDING_WITH_BORDER = INPUT_PADDING_BASE * 2 + 4;

export const SliderTextInput = ({
  value,
  precision,
  suffix,
  onChange,
  isValueValid,
  anchorEl,
  boundaryEl,
  id,
}: Props) => {
  const { ref: suffixRef, width: suffixWidth } = useResizeObserver();
  const { ref: inputMaskRef, width: inputMaskWidth } = useResizeObserver();
  const deferredInputMaskWidth = useDeferredValue(inputMaskWidth);
  const [input, setInput] = useState('');
  const inputNumber = parseNumberSafe(input, precision);
  const isValid = isValidNumber(input) && isValueValid(inputNumber);

  const handleChangeManual = (e: FormEvent<HTMLInputElement>) => {
    const current = e.currentTarget.value;
    const newValue = stripFractionsExceedingDecimals(
      processRealNumberInput(current),
      precision
    );
    setInput(newValue);
  };
  const handleBlur = () => {
    if (!isValid) {
      setInput(roundNumber(value, precision).toString());
      return;
    }

    onChange(inputNumber);
  };

  useEffect(() => {
    setInput(roundNumber(value, precision).toString());
  }, [value, precision]);

  const targetSuffixWidth =
    suffix && suffixWidth ? suffixWidth + INPUT_PADDING_BASE : 0;

  const textInputWidth =
    (deferredInputMaskWidth ?? 30) +
    INPUT_PADDING_WITH_BORDER +
    targetSuffixWidth;

  const isMeasured =
    typeof suffixWidth === 'number' &&
    typeof deferredInputMaskWidth === 'number';

  return (
    <>
      <Popper
        anchorEl={anchorEl}
        boundaryEl={boundaryEl}
        updateKey={`${value}_${isMeasured}`}
        offsetX={0}
        offsetY={16}
      >
        <div
          className={cn(
            'max-w-[300px] transition-opacity duration-300',
            isMeasured ? 'opacity-100' : 'opacity-0'
          )}
          style={{
            width: `${textInputWidth}px`,
          }}
        >
          <Textfield
            id={id}
            styleType="compact"
            error={!isValid ? ' ' : undefined}
            type="tel"
            inputMode="decimal"
            pattern="[0-9]*"
            value={input}
            onChange={handleChangeManual}
            onBlur={handleBlur}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            postfix={
              <div className="whitespace-nowrap" ref={suffixRef}>
                {suffix}
              </div>
            }
          />
        </div>
      </Popper>
      <span
        className="invisible fixed left-[-9999px] inline-block text-xs"
        ref={inputMaskRef}
      >
        {input || '0'}
      </span>
    </>
  );
};
